import {
  browserFishydexCurrentRepository,
  FISHYDEX_CURRENT_BRAND,
} from "./current-repository.js";
import {
  browserFishydexDeviceUiRepository,
  FISHYDEX_DEVICE_UI_BRAND,
} from "./device-ui-repository.js";
import {
  browserFishydexGuideEvidenceRepository,
  FISHYDEX_GUIDE_EVIDENCE_BRAND,
} from "./guide-evidence-repository.js";
import { fishydexBrowserStorage } from "./browser-storage-provider.js";

const SLOT = "__fishystuffFishydexStateCompositionV1";
export const FISHYDEX_STATE_COMPOSITION_BRAND = Symbol.for(
  "fishystuff.fishydex-state-composition.v1",
);

function requireRepository(repository, brand, methods, label) {
  if (repository?.[brand] !== 1) {
    throw new TypeError(`${label} has an incompatible brand`);
  }
  for (const method of methods) {
    if (typeof repository[method] !== "function") {
      throw new TypeError(`${label} is missing ${method}()`);
    }
  }
  return repository;
}

function failure(result, operation) {
  return Object.freeze({
    kind: result?.kind ?? "unavailable",
    operation,
    reason: result?.reason ?? null,
    authoritative: result?.authoritative ?? null,
  });
}

export function createFishydexStateComposition({
  currentRepository,
  deviceUiRepository,
  guideEvidenceRepository,
} = {}) {
  const current = requireRepository(
    currentRepository,
    FISHYDEX_CURRENT_BRAND,
    ["dispose", "exportLegacyRaw", "exportRawCollection", "load", "resetCurrentData", "state"],
    "Fishydex Current repository",
  );
  const ui = requireRepository(
    deviceUiRepository,
    FISHYDEX_DEVICE_UI_BRAND,
    ["discardLegacy", "dispose", "exportRaw", "load", "reset"],
    "Fishydex device-UI repository",
  );
  const evidence = requireRepository(
    guideEvidenceRepository,
    FISHYDEX_GUIDE_EVIDENCE_BRAND,
    ["dispose", "exportRaw", "load", "reset"],
    "Fishydex Guide evidence repository",
  );
  let disposed = false;

  function alive() {
    if (disposed) {
      throw new Error("Fishydex state composition is disposed");
    }
  }

  async function load() {
    alive();
    const currentState = await current.load();
    const uiState = ui.load();
    const evidenceState = evidence.load();
    return Object.freeze({
      kind: [currentState, uiState, evidenceState].every((value) => (
        value.kind === "ready" || value.kind === "missing"
      )) ? "ready" : "unavailable",
      current: currentState,
      ui: uiState,
      evidence: evidenceState,
    });
  }

  async function resetData() {
    alive();
    // The shared legacy UI record also carried showDried. Remove it before
    // clearing Current so a later bootstrap cannot re-import semantic intent.
    const legacyUi = await ui.discardLegacy();
    if (legacyUi.kind !== "missing") {
      return failure(legacyUi, "discard-legacy-ui-before-data-reset");
    }
    const currentState = await current.resetCurrentData();
    if (currentState.kind !== "ready") {
      return failure(currentState, "reset-fishydex-current");
    }
    const evidenceState = await evidence.reset();
    if (evidenceState.kind !== "missing") {
      return failure(evidenceState, "reset-guide-evidence");
    }
    // Evidence reset synchronously updates Current through its repository
    // subscription. Return that post-reset acknowledgement rather than the
    // pre-reset projection captured above.
    const acknowledgedCurrent = current.state();
    if (acknowledgedCurrent.kind !== "ready") {
      return failure(acknowledgedCurrent, "acknowledge-current-after-evidence-reset");
    }
    return Object.freeze({
      kind: "ready",
      current: acknowledgedCurrent,
      evidence: evidenceState,
    });
  }

  async function resetUi() {
    alive();
    const uiState = await ui.reset();
    return uiState.kind === "missing"
      ? Object.freeze({ kind: "missing", ui: uiState })
      : failure(uiState, "reset-fishydex-device-ui");
  }

  async function reset() {
    alive();
    const data = await resetData();
    if (data.kind !== "ready") {
      return data;
    }
    const uiState = await resetUi();
    if (uiState.kind !== "missing") {
      return uiState;
    }
    return Object.freeze({ kind: "missing", data, ui: uiState });
  }

  async function exportLocal() {
    alive();
    const workspace = await current.exportRawCollection();
    return Object.freeze({
      current: current.state(),
      workspace,
      legacy: current.exportLegacyRaw(),
      ui: ui.exportRaw(),
      evidence: evidence.exportRaw(),
    });
  }

  return Object.freeze({
    [FISHYDEX_STATE_COMPOSITION_BRAND]: 1,
    current,
    ui,
    evidence,
    load,
    resetData,
    resetUi,
    reset,
    exportLocal,
    dispose() {
      if (disposed) {
        return;
      }
      disposed = true;
      // The composition is the lifecycle owner of the three repositories it
      // receives. Current must unsubscribe from evidence before evidence tears
      // down its provider listeners.
      current.dispose();
      ui.dispose();
      evidence.dispose();
    },
  });
}

export function browserFishydexStateComposition(options = {}) {
  const windowRef = options.window ?? globalThis.window;
  const occupied = windowRef?.[SLOT];
  if (occupied !== undefined) {
    if (occupied?.[FISHYDEX_STATE_COMPOSITION_BRAND] !== 1) {
      throw new TypeError("Shared Fishydex state composition is incompatible");
    }
    return occupied;
  }
  const storage = fishydexBrowserStorage(options.storage);
  const evidence = options.guideEvidenceRepository
    ?? browserFishydexGuideEvidenceRepository({
      window: windowRef,
      storage,
      eventTarget: options.eventTarget ?? windowRef,
      fishStateContract: options.fishStateContract
        ?? windowRef?.__fishystuffSharedFishState,
    });
  const composition = createFishydexStateComposition({
    guideEvidenceRepository: evidence,
    deviceUiRepository: options.deviceUiRepository
      ?? browserFishydexDeviceUiRepository({
        window: windowRef,
        storage,
        eventTarget: options.eventTarget ?? windowRef,
      }),
    currentRepository: options.currentRepository
      ?? browserFishydexCurrentRepository({
        window: windowRef,
        storage,
        eventTarget: options.eventTarget ?? windowRef,
        workspace: options.workspace,
        evidenceRepository: evidence,
        fishStateContract: options.fishStateContract
          ?? windowRef?.__fishystuffSharedFishState,
      }),
  });
  if (windowRef && typeof windowRef === "object") {
    windowRef[SLOT] = composition;
  }
  return composition;
}
